import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSchemas } from "@/contexts/SchemaContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import FieldForm from "@/components/schema/FieldForm";
import ReachPanel from "@/components/schema/ReachPanel";
import ImportExport from "@/components/schema/ImportExport";
import HistoryDialog from "@/components/schema/HistoryDialog";
import { useReviewFlow } from "@/components/schema/ReviewFlow";
import { useConfirm } from "@/components/ConfirmDialog";
import { defaultsFor, downloadJson, fmtMeta, resolveFields } from "@/lib/schema";

const LABEL = "text-xs uppercase tracking-[0.15em] text-slate-600";

// Admin > Structure > Therapies. Admins and therapists manage therapies.
export default function Therapies() {
  const { modules: mdata, patient: pdata } = useSchemas();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [hist, setHist] = useState(null);
  const [links, setLinks] = useState(null);
  const { user } = useAuth();
  const confirmDialog = useConfirm();

  const load = async () => {
    try { const { data } = await api.get("/therapies"); setRows(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); api.get("/modules/overview").then((r) => setLinks(r.data)).catch(() => {}); }, []);
  const unavailable = (id) => !!links && (links.missing_modules || []).concat(Object.entries(links.modules || {}).filter(([, v]) => !v.simulations.length).map(([k]) => k)).includes(id);

  const remove = async (t) => {
    const ok = await confirmDialog({ title: "Delete therapy?", description: `"${t.Name}" will be removed. Patients already using it keep their settings.`, confirmText: "Delete", destructive: true });
    if (!ok) return;
    try { await api.delete(`/therapies/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const exportOne = async (t) => {
    try { const { data } = await api.get(`/therapies/${t.id}/export`); downloadJson(data, `therapy-${t.Name.replace(/\W+/g, "_")}.json`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="therapies">
      <WhatIsTherapy />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-500">A therapy is a reusable plan: module configs plus optional patient settings. Apply it from a patient page.</p>
        <div className="flex gap-2">
          {/* Import creates a new therapy */}
          {user.role === "admin" && <ImportExport exportUrl="/therapies" filename="" history={null}
            importCall={({ dryRun, allowOlder }, json) => api.post("/therapies/import", json, { params: { dry_run: dryRun, allow_older: allowOlder } })}
            onChanged={load} hideExport />}
          <Button className="btn-brand rounded-full" onClick={() => setEditing({ id: null, Name: "", Description: "", Settings: {}, Modules: [] })} data-testid="new-therapy">
            <Plus className="h-4 w-4 mr-2" /> New therapy
          </Button>
        </div>
      </div>
      <div className="panel rounded-2xl divide-y divide-slate-100">
        {rows.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No therapies yet.</div>}
        {rows.map((t) => (
          <div key={t.id} className="px-4 py-3 flex items-center gap-3" data-testid={`therapy-row-${t.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900 truncate">{t.Name}</div>
              <div className="text-xs text-slate-500 truncate">{t.Description}</div>
            </div>
            <div className="flex flex-wrap gap-1 max-w-sm justify-end">
              {(t.Modules || []).map((m) => (
                <Badge key={m.ModuleId} variant="outline" className={`text-[10px] ${unavailable(m.ModuleId) ? "bg-amber-50 text-amber-800 border-amber-200" : ""}`}
                  title={unavailable(m.ModuleId) ? "Not available on headsets yet: this therapy cannot be applied until a simulation is linked" : ""}
                  data-testid={`th-chip-${t.id}-${m.ModuleId}`}>
                  {m.ModuleId}{m.Level ? ` L${m.Level}` : ""}{unavailable(m.ModuleId) ? " (not available yet)" : ""}
                </Badge>
              ))}
              {(t.Modules || []).length === 0 && <span className="text-xs text-slate-400">settings only</span>}
            </div>
            <span className="text-xs text-slate-400 hidden md:block">{fmtMeta(t.Meta)}</span>
            {user.role === "admin" && <Button variant="ghost" size="sm" onClick={() => exportOne(t)} title="Export"><Download className="h-4 w-4" /></Button>}
            <Button variant="ghost" size="sm" onClick={() => setHist(t.id)}>History</Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing(t)} data-testid={`edit-therapy-${t.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => remove(t)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
          </div>
        ))}
      </div>
      {editing && <TherapyDialog initial={editing} mdata={mdata} pdata={pdata} unavailable={unavailable} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {hist && <HistoryDialog open onClose={() => setHist(null)} kind="therapy" recordId={hist} canRestore onRestored={load} />}
    </div>
  );
}

// Optional patient treatment settings carried by a therapy: tick an area, then set its values like on the patient page.
function SettingsPicker({ pdata, value, onChange, role }) {
  const groups = resolveFields(pdata?.patientInputs || [], pdata?.arcFields || []).filter((g) => g.type === "group" && g.panel !== "patientFields");
  const toggle = (g, on) => {
    const next = { ...value };
    if (on) next[g.key] = defaultsFor(g.fields, pdata); else delete next[g.key];
    onChange(next);
  };
  return (
    <div data-testid="th-settings">
      <Label className={LABEL}>Patient treatment settings (optional)</Label>
      <p className="text-xs text-slate-500 mt-1 mb-2">
        Tick a treatment area to include its settings. When the therapy is applied, those settings replace the patient's own for that area
        (for example: tick Speech and switch off "Allow level decreases"). Leave everything unticked to leave the patient's settings alone.
      </p>
      {groups.map((g) => (
        <div key={g.key} className="border border-slate-200 rounded-lg p-3 mb-2">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={!!value?.[g.key]} onChange={(e) => toggle(g, e.target.checked)} data-testid={`th-include-${g.key}`} />
            Include {g.label || g.key} settings
          </label>
          {value?.[g.key] && (
            <div className="mt-3">
              {g.panel === "reach"
                ? <ReachPanel group={g} values={value[g.key]} role={role} patientData={pdata} onChange={(v) => onChange({ ...value, [g.key]: v })} />
                : <FieldForm fields={g.fields} values={value[g.key]} role={role} patientData={pdata} onChange={(v) => onChange({ ...value, [g.key]: v })} />}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function WhatIsTherapy() {
  return (
    <div className="panel rounded-2xl p-5 text-sm text-slate-700 space-y-2" data-testid="what-is-therapy">
      <div className="font-display text-base font-semibold text-slate-900">What does a therapy do?</div>
      <p>A therapy is a <b>saved treatment plan</b> you can reuse. Applying it to a patient sets up their headset in one step:</p>
      <ul className="list-disc pl-5 space-y-0.5">
        <li>which <b>activities</b> are on the headset (for example Reach and Brick Breaker),</li>
        <li>each activity's <b>difficulty level</b>, the <b>zones</b> where targets may appear, and whether the headset may adjust the level itself,</li>
        <li>optionally the patient's <b>treatment settings</b> (for example the body size preset).</li>
      </ul>
      <p>Before anything changes you see a <b>preview of every change</b>, and nothing is saved if there is a problem. A therapy never changes the patient's name, notes, progress or history, and any change can be undone from History.</p>
      <p className="text-slate-500">Example: "Left arm early recovery" = Reach at Level 1 on the left-hand zones, plus Brick Breaker at Level 1. Build one here, or open a patient and choose Therapy &gt; Save current plan as therapy.</p>
    </div>
  );
}

function TherapyDialog({ initial, mdata, pdata, unavailable, onClose, onSaved }) {
  const { user } = useAuth();
  const [t, setT] = useState({ ...initial, settings: initial.Settings || {} });
  const flow = useReviewFlow(onSaved);
  const inputs = resolveFields(mdata?.moduleInputs || [], []);
  const moduleOf = (id) => (mdata?.modules || []).find((m) => m.id === id);
  const fieldsFor = (id) => inputs.filter((f) => f.key !== "ModuleId").filter((f) => {
    const mod = moduleOf(id);
    return !f.onlyIfDomain || ((mod?.domains || []).includes(f.onlyIfDomain) && !mod?.tbd);
  });
  const addModule = (id) => {
    const base = defaultsFor(inputs.filter((f) => f.key !== "ModuleId" && (!f.onlyIfDomain || (moduleOf(id)?.domains || []).includes(f.onlyIfDomain))), pdata);
    setT({ ...t, Modules: [...t.Modules, { ModuleId: id, ...base }] });
  };
  const setModule = (i, v) => setT({ ...t, Modules: t.Modules.map((m, k) => (k === i ? v : m)) });

  const build = () => {
    const settings = t.settings || {};
    return { Name: t.Name, Description: t.Description || "", Settings: settings,
      Modules: t.Modules.map(({ Meta, ...rest }) => rest), ...(t.Meta ? { Meta: t.Meta } : {}) };
  };
  const submit = () => {
    const body = build();
    if (!body) return;
    flow.start(t.id ? "Save therapy" : "Create therapy", ({ dryRun, allowOlder }) => t.id
      ? api.patch(`/therapies/${t.id}`, body, { params: { dry_run: dryRun, allow_older: allowOlder } })
      : api.post("/therapies", body, { params: { dry_run: dryRun } }));
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="therapy-dialog">
        <DialogHeader><DialogTitle className="font-display">{t.id ? "Edit therapy" : "New therapy"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label className={LABEL}>Name</Label><Input value={t.Name} onChange={(e) => setT({ ...t, Name: e.target.value })} data-testid="th-name" /></div>
            <div><Label className={LABEL}>Description</Label><Input value={t.Description || ""} onChange={(e) => setT({ ...t, Description: e.target.value })} /></div>
          </div>
          <SettingsPicker pdata={pdata} value={t.settings} role={user.role} onChange={(settings) => setT({ ...t, settings })} />
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className={LABEL}>Modules</Label>
              <Select value="" onValueChange={addModule}>
                <SelectTrigger className="w-52 h-8" data-testid="th-add-module"><SelectValue placeholder="Add module…" /></SelectTrigger>
                <SelectContent>
                  {(mdata?.modules || []).filter((m) => !t.Modules.some((x) => x.ModuleId === m.id)).map((m) => <SelectItem key={m.id} value={m.id}>{m.name || m.id}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {t.Modules.length === 0 && <div className="text-xs text-slate-500">No modules yet.</div>}
            {t.Modules.map((m, i) => (
              <div key={m.ModuleId} className="border border-purple-100 rounded-xl p-4 mb-3 bg-white/60" data-testid={`th-module-${m.ModuleId}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-medium">{moduleOf(m.ModuleId)?.name || m.ModuleId} <span className="font-mono text-xs text-slate-400">{m.ModuleId}</span></div>
                  <Button variant="ghost" size="sm" onClick={() => setT({ ...t, Modules: t.Modules.filter((_, k) => k !== i) })}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
                {unavailable(m.ModuleId) && (
                  <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900" data-testid={`th-unavailable-${m.ModuleId}`}>
                    This activity is not available on headsets yet (no simulation is linked to it), so this therapy cannot be applied to a patient until it is. Remove it, or ask an admin to link a simulation.
                  </div>
                )}
                <FieldForm fields={fieldsFor(m.ModuleId)} values={m} onChange={(v) => setModule(i, v)} role={user.role} patientData={pdata} />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="btn-brand rounded-full" disabled={!t.Name.trim()} onClick={submit} data-testid="th-save">Review and save</Button>
        </DialogFooter>
        {flow.dialog}
      </DialogContent>
    </Dialog>
  );
}
